import type {
  MissionItem,
  PointCloudItem,
  TrajectoryItem,
} from '@mobile-mapping/providers';
import type { MapWorkspace } from '@trimble/geospatial';
import type { ProjectDataRepository } from '@trimble/project-data-repository';

import {
  MISSION_ROOT_NODE_ID,
  MODEL_ROOT_NODE_ID,
  POINT_CLOUD_ROOT_NODE_ID,
  TRAJECTORY_ROOT_NODE_ID,
} from './project-data-tree-nodes';
import {
  getWorkspaceIdFromLayerNodeId,
  MAP_WORKSPACES_ROOT_NODE_ID,
} from './workspace-tree-nodes';

export type FxWorkspaceFolderDescendantRefTrnsGetter = (
  nodeId: string
) => readonly string[];

export function createMissionFolderDescendantRefTrnsGetter(
  missions: readonly MissionItem[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  // Only the missions root aggregates every mission. A mission reference is its
  // mission TRN. Nothing deeper than the mission node is selectable, so a
  // single mission node never reports descendants here.
  const allMissionTrns = missions.map((mission) => mission.missionTrn);

  return (nodeId: string): readonly string[] =>
    nodeId === MISSION_ROOT_NODE_ID ? allMissionTrns : [];
}

export function collectModelFolderDescendantRefTrns(
  repository: ProjectDataRepository,
  nodeId: string
): readonly string[] {
  const storeParentId =
    nodeId === MODEL_ROOT_NODE_ID ? repository.rootId : nodeId;
  const refTrns: string[] = [];

  const visit = (parentId: string): void => {
    for (const childId of repository.getChildren(parentId)) {
      const child = repository.getNode(childId);
      if (child === undefined || child.isDeleted) {
        continue;
      }

      if (child.type === 'FOLDER') {
        visit(childId);
        continue;
      }

      refTrns.push(childId);
    }
  };

  visit(storeParentId);
  return refTrns;
}

export function createPointCloudFolderDescendantRefTrnsGetter(
  items: readonly PointCloudItem[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  const standaloneRefTrns = items
    .filter((item) => item.mission === undefined)
    .map((item) => item.id);

  return (nodeId: string): readonly string[] =>
    nodeId === POINT_CLOUD_ROOT_NODE_ID ? standaloneRefTrns : [];
}

export function createTrajectoryFolderDescendantRefTrnsGetter(
  items: readonly TrajectoryItem[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  const standaloneRefTrns = items
    .filter((item) => item.mission === undefined)
    .map((item) => item.id);

  return (nodeId: string): readonly string[] =>
    nodeId === TRAJECTORY_ROOT_NODE_ID ? standaloneRefTrns : [];
}

function savedLayerRefTrnsForWorkspace(
  workspaceId: string,
  draftRefTrns: ReadonlySet<string>
): readonly string[] {
  const savedLayerRefTrns: string[] = [];
  for (const refTrn of draftRefTrns) {
    if (getWorkspaceIdFromLayerNodeId(refTrn) === workspaceId) {
      savedLayerRefTrns.push(refTrn);
    }
  }
  return savedLayerRefTrns;
}

export function mapWorkspaceCheckboxRefTrns(
  workspaceId: string,
  draftRefTrns: ReadonlySet<string>
): readonly string[] {
  // Older saves stored a layer node id. Keep it selected with this workspace
  // until the user clears the checkbox, so edit-and-save can remove it.
  const savedLayerRefTrns = savedLayerRefTrnsForWorkspace(
    workspaceId,
    draftRefTrns
  );
  if (draftRefTrns.has(workspaceId) || savedLayerRefTrns.length === 0) {
    return [workspaceId, ...savedLayerRefTrns];
  }

  return savedLayerRefTrns;
}

export function mapWorkspaceFolderCheckboxRefTrns(
  workspaceIds: readonly string[],
  draftRefTrns: ReadonlySet<string>
): readonly string[] {
  return workspaceIds.flatMap((workspaceId) =>
    mapWorkspaceCheckboxRefTrns(workspaceId, draftRefTrns)
  );
}

export function createMapWorkspaceFolderDescendantRefTrnsGetter(
  workspaces: readonly MapWorkspace[]
): FxWorkspaceFolderDescendantRefTrnsGetter {
  const workspaceIds = workspaces.map((workspace) => workspace.id);

  return (nodeId: string): readonly string[] =>
    nodeId === MAP_WORKSPACES_ROOT_NODE_ID ? workspaceIds : [];
}
